/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Link, Typography2 as Typography, breakpoints } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import useLohpCollection from 'bundles/front-page/hooks/useLohpCollection';
import CompactCollectionProductCard from 'bundles/front-page/redesign-modules/compact-collections/CompactCollectionProductCard';
import CompactCollectionsPlaceholder from 'bundles/front-page/redesign-modules/compact-collections/CompactCollectionsPlaceholder';

type Props = {
  headline: string;
  headlineTarget: string;
  collection: {
    id: string;
    collectionModuleId: string;
    presetCollectionId?: string;
    filterName?: string;
    collectionName?: string;
    facets?: Array<Array<string>>;
  };
};

const styles = {
  container: css`
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: calc(var(--cds-spacing-150) / 2 + var(--cds-spacing-100));
    background-color: var(--cds-color-emphasis-primary-background-weak);
    padding: calc(var(--cds-spacing-150) / 2 + var(--cds-spacing-100));
    border-radius: calc(var(--cds-border-radius-300) / 2);

    ${breakpoints.down('sm')} {
      padding: calc(var(--cds-spacing-100) + var(--cds-spacing-50));
      gap: var(--cds-spacing-100);
    }
  `,
  collectionHeadline: css`
    display: flex;
    justify-content: space-between;

    .cds-link-icon {
      display: none;
      height: 20px;
      color: var(--cds-color-emphasis-neutral-background-xxstrong);
    }

    &:hover {
      text-decoration: none;
    }

    &:hover .cds-link-icon {
      display: inline;
    }

    &:focus .cds-link-icon {
      display: inline;
    }

    ${breakpoints.down('sm')} {
      .cds-link-icon {
        display: inline;
      }
    }
  `,
};

const CompactCollection = ({ headline, headlineTarget, collection }: Props) => {
  const isPresetCollection = !!collection.presetCollectionId;

  const { collection: collectionData, loading: isCollectionsLoading } = useLohpCollection({
    limit: 3,
    queryCollectionId: collection.collectionModuleId,
    additionalInput: isPresetCollection
      ? {
          preset_collection_id: collection.presetCollectionId,
        }
      : {
          collection_name: collection.collectionName,
          facets: collection.facets,
        },
  });

  if (!isCollectionsLoading && !collectionData?.entities.length) return null;

  if (isCollectionsLoading) return <CompactCollectionsPlaceholder />;

  return (
    <div css={styles.container}>
      <Link
        href={headlineTarget}
        variant="quiet"
        icon={<ArrowNextIcon size="small" />}
        iconPosition="after"
        css={styles.collectionHeadline}
      >
        <Typography variant="subtitleMedium" component="h3">
          {headline}
        </Typography>
      </Link>
      <Grid container direction="column" spacing={8}>
        {collectionData?.entities.map((entity, index) => (
          <Grid item key={entity.id}>
            <CompactCollectionProductCard
              entity={entity}
              entityIndex={index}
              additionalV3TrackingData={{
                carousel: {
                  id: collectionData?.collectionId ?? '',
                  name: headline ?? '',
                  recommender: collectionData?.recommender,
                },
              }}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default CompactCollection;
